import { API_ROUTES, type MeResponse, type SwaClientPrincipal } from '@oscom/shared';
import { ApiRequestError, apiGet } from '../api/client';
import { fetchClientPrincipal } from './swa-auth';

/**
 * Session loading logic, kept free of React so it can be unit tested.
 *
 * Two steps on purpose:
 * 1. `/.auth/me` - is there a platform session at all?
 * 2. `/api/me`   - what does the SERVER say about identity and role?
 *
 * Step 2 is authoritative. If it answers 403 the user is signed in but not
 * allowed in (e.g. a disabled account) - that is a different state than
 * "not signed in" and gets its own page.
 */

export type Session =
  | { status: 'anonymous' }
  | { status: 'authenticated'; principal: SwaClientPrincipal; me: MeResponse }
  | { status: 'denied'; message: string }
  | { status: 'error'; message: string; correlationId?: string };

export interface SessionDependencies {
  loadPrincipal: () => Promise<SwaClientPrincipal | null>;
  loadMe: () => Promise<MeResponse>;
}

const defaultDependencies: SessionDependencies = {
  loadPrincipal: fetchClientPrincipal,
  loadMe: () => apiGet<MeResponse>(API_ROUTES.me),
};

export async function loadSession(
  dependencies: SessionDependencies = defaultDependencies,
): Promise<Session> {
  let principal: SwaClientPrincipal | null;
  try {
    principal = await dependencies.loadPrincipal();
  } catch {
    return { status: 'error', message: 'Die Anmeldeinformationen konnten nicht geladen werden.' };
  }

  if (!principal) {
    return { status: 'anonymous' };
  }

  try {
    const me = await dependencies.loadMe();
    return { status: 'authenticated', principal, me };
  } catch (error) {
    if (error instanceof ApiRequestError) {
      if (error.isUnauthenticated) {
        // Platform cookie exists but is no longer accepted - treat as signed out.
        return { status: 'anonymous' };
      }
      if (error.isForbidden) {
        return { status: 'denied', message: error.message };
      }
      return {
        status: 'error',
        message: error.message,
        ...(error.correlationId ? { correlationId: error.correlationId } : {}),
      };
    }
    return { status: 'error', message: 'Unerwarteter Fehler beim Laden der Sitzung.' };
  }
}
